import React, { useState, useCallback } from 'react';
import { useNavigate, Link as RouterLink, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useStore } from '../../context/StoreContext';
import { buildCustomerRoute } from '../../utils/storeUtils';
import {
  TextField,
  Box,
  Alert,
  Typography,
  CircularProgress,
  Link,
} from '@mui/material';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import AuthHeaderActions from '../../components/auth/AuthHeaderActions';
import StoreAuthLogo, { getStoreAuthBackgroundSx } from '../../components/auth/StoreAuthBranding';
import PageLoadingSpinner from '../../components/common/PageLoadingSpinner';
import PrimaryActionButton from '../../components/common/PrimaryActionButton';
import PasswordField from '../../components/auth/PasswordField';
import GoogleSignInSection from '../../components/auth/GoogleSignInSection';
import useCustomerGoogleAuth from '../../hooks/useCustomerGoogleAuth';
import LegalTermsCheckbox from '../../components/legal/LegalTermsCheckbox';
import LegalFooterLinks from '../../components/legal/LegalFooterLinks';
import { getBackendLanguageId, getPreferredLanguage } from '../../constants/Languages';
import { BRAND } from '../../theme/brandTokens';
import { brandBackdropSx, brandCardSx } from '../../theme/brandSurfaces';

const backgroundStyle = {
  minHeight: { xs: '100dvh', sm: '100vh' },
  width: '100%',
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'center',
  ...brandBackdropSx,
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  overflowY: 'auto',
  overflowX: 'hidden',
  WebkitOverflowScrolling: 'touch',
  py: { xs: 2, sm: 0 },
  px: 0,
  touchAction: 'pan-y',
};

const cardStyle = {
  padding: { xs: '2rem 1.5rem', sm: '2.5rem 2rem' },
  ...brandCardSx,
  minWidth: { xs: 300, sm: 400 },
  maxWidth: { xs: 350, sm: 450 },
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
};

const logoStyle = {
  fontWeight: 800,
  fontSize: { xs: '1.5rem', sm: '2rem' },
  letterSpacing: 2,
  color: BRAND.primaryText,
  marginBottom: '1.5rem',
  textAlign: 'center',
  width: '100%',
  maxWidth: '100%',
  lineHeight: 1.2,
  overflowWrap: 'anywhere',
  wordBreak: 'break-word',
};

const CustomerRegisterPage = () => {
  const { t } = useTranslation();
  const { registerCustomer } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { storeId, storeName, storeOverview, loading: storeLoading } = useStore();
  const loginLogoUrl = storeOverview?.brandingImages?.loginLogoUrl;
  const loginBackgroundUrl = storeOverview?.brandingImages?.loginBackgroundUrl;
  const prefilledEmail = location.state?.email?.trim() || new URLSearchParams(location.search).get('email')?.trim() || '';
  const [form, setForm] = useState({
    name: '',
    email: prefilledEmail,
    password: '',
    confirmPassword: '',
  });
  const [error, setError] = useState('');
  const [legalTermsError, setLegalTermsError] = useState('');
  const [acceptedLegalTerms, setAcceptedLegalTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const redirectAfterGoogleAuth = useCallback(() => {
    setSuccess(true);
    const redirectPath = storeId
      ? buildCustomerRoute(storeId, '')
      : '/not-found';

    setTimeout(() => {
      navigate(redirectPath, { replace: true });
    }, 1500);
  }, [navigate, storeId]);

  const { completeGoogleSignIn, googleLoading } = useCustomerGoogleAuth({
    onSuccess: redirectAfterGoogleAuth,
    onError: () => setError('register.failed'),
  });

  const handleGoogleSignInCallback = useCallback(async (response) => {
    setError('');
    await completeGoogleSignIn(response?.credential);
  }, [completeGoogleSignIn]);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLegalTermsError('');

    if (!acceptedLegalTerms) {
      setLegalTermsError('legal.registration.termsRequired');
      return;
    }

    if (!form.name || !form.email || !form.password || !form.confirmPassword) {
      setError('register.required');
      return;
    }

    if (form.password !== form.confirmPassword) {
      setError('register.passwordMismatch');
      return;
    }

    if (form.password.length < 6) {
      setError('register.passwordTooShort');
      return;
    }

    setLoading(true);
    const registerSuccess = await registerCustomer({
      name: form.name,
      email: form.email,
      password: form.password,
      language: getBackendLanguageId(getPreferredLanguage()),
    });
    setLoading(false);

    if (registerSuccess) {
      setSuccess(true);
      const redirectPath = storeId
        ? buildCustomerRoute(storeId, '')
        : '/not-found';

      setTimeout(() => {
        navigate(redirectPath, { replace: true });
      }, 1500);
    } else {
      setError('register.failed');
    }
  };

  if (storeLoading) {
    return <PageLoadingSpinner />;
  }

  return (
    <Box
      sx={{ ...backgroundStyle, ...getStoreAuthBackgroundSx(loginBackgroundUrl) }}
      onTouchMove={(e) => {
        e.stopPropagation();
      }}
    >
      <Box sx={{ display: { xs: 'none', sm: 'block' }, position: 'absolute', top: 24, right: 24 }}>
        <AuthHeaderActions />
      </Box>
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          flex: 1,
        }}
      >
        <Box
          sx={{
            display: { xs: 'flex', sm: 'none' },
            width: '100%',
            maxWidth: { xs: 350, sm: 450 },
            justifyContent: 'flex-end',
            alignItems: 'center',
            px: 3,
            pt: 2,
            pb: 2,
            minHeight: 56,
            boxSizing: 'border-box',
          }}
        >
          <AuthHeaderActions />
        </Box>
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            pt: { xs: 0, sm: 3 },
            pb: { xs: 'calc(1rem + env(safe-area-inset-bottom, 0px) + 60px)', sm: 2 },
          }}
        >
          <Box
            component={motion.div}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            sx={cardStyle}
          >
            {loginLogoUrl ? (
              <StoreAuthLogo imageUrl={loginLogoUrl} storeName={storeName} />
            ) : storeName ? (
              <Typography sx={logoStyle} variant="h4" component="div">
                {storeName}
              </Typography>
            ) : null}
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 2, textAlign: 'center' }}>
              {t('register.title') || 'Create Account'}
            </Typography>

            {error && (
              <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
                {t(error)}
              </Alert>
            )}
            {success && (
              <Alert severity="success" sx={{ width: '100%', mb: 2 }}>
                {t('register.success') || 'Account created successfully!'}
              </Alert>
            )}

            <Box component="form" noValidate onSubmit={handleSubmit} sx={{ width: '100%', px: 1, pb: { xs: 1, sm: 0 } }}>
              <TextField
                margin="normal"
                required
                fullWidth
                id="name"
                label={t('register.name') || 'Full Name'}
                name="name"
                autoComplete="name"
                autoFocus
                value={form.name}
                onChange={handleChange}
                sx={{ mb: 2, borderRadius: 2 }}
                InputProps={{
                  style: { borderRadius: 12 },
                }}
              />
              <TextField
                margin="normal"
                required
                fullWidth
                id="email"
                label={t('register.email') || 'Email Address'}
                name="email"
                autoComplete="email"
                value={form.email}
                onChange={handleChange}
                sx={{ mb: 2, borderRadius: 2 }}
                InputProps={{
                  style: { borderRadius: 12 },
                }}
              />
              <PasswordField
                margin="normal"
                required
                fullWidth
                name="password"
                label={t('register.password') || 'Password'}
                id="password"
                autoComplete="new-password"
                value={form.password}
                onChange={handleChange}
                sx={{ mb: 2, borderRadius: 2 }}
                InputProps={{
                  style: { borderRadius: 12 },
                }}
              />
              <PasswordField
                margin="normal"
                required
                fullWidth
                name="confirmPassword"
                label={t('register.confirmPassword') || 'Confirm Password'}
                id="confirmPassword"
                autoComplete="new-password"
                value={form.confirmPassword}
                onChange={handleChange}
                sx={{ mb: 2, borderRadius: 2 }}
                InputProps={{
                  style: { borderRadius: 12 },
                }}
              />

              <LegalTermsCheckbox
                checked={acceptedLegalTerms}
                onChange={(value) => {
                  setAcceptedLegalTerms(value);
                  if (value) {
                    setLegalTermsError('');
                  }
                }}
                error={legalTermsError}
              />

              <PrimaryActionButton
                type="submit"
                fullWidth
                sx={{
                  mt: 2,
                  mb: 2,
                  py: 1.5,
                  fontSize: '1.1rem',
                }}
                disabled={loading || googleLoading || success || !acceptedLegalTerms}
                endIcon={loading && <CircularProgress size={22} color="inherit" />}
              >
                {loading ? (t('register.creating') || 'Creating Account...') : (t('register.create') || 'Create Account')}
              </PrimaryActionButton>

              <GoogleSignInSection
                onCredential={handleGoogleSignInCallback}
                loading={googleLoading}
                buttonText="signup_with"
              />

              <Box sx={{ textAlign: 'center', width: '100%' }}>
                <Link
                  component={RouterLink}
                  to={storeId ? buildCustomerRoute(storeId, 'login') : '/not-found'}
                  sx={{
                    color: BRAND.accentText,
                    textDecoration: 'none',
                    fontSize: '0.875rem',
                    '&:hover': {
                      textDecoration: 'underline',
                    },
                  }}
                >
                  {t('register.haveAccount') || 'Already have an account? Sign in'}
                </Link>
              </Box>

              <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
                <LegalFooterLinks />
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default CustomerRegisterPage;
